import { useEffect, useState } from 'react';

export default function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative w-full overflow-hidden"
      style={{ minHeight: '100vh' }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://lirp.cdn-website.com/785a78af/dms3rep/multi/opt/Shadow-roofing-and-solar-5-1920w.webp"
          alt="Shadowfax Roofing & Solar — premium roofing and solar installation in Campbell, CA"
          className="w-full h-full object-cover object-center"
        />
        {/* Layered overlays for cinematic depth */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(4,10,20,0.96) 0%, rgba(6,15,26,0.82) 45%, rgba(6,15,26,0.45) 100%)' }}></div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(4,10,20,0.90) 0%, transparent 50%, rgba(4,10,20,0.30) 100%)' }}></div>
        {/* Subtle noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")', backgroundRepeat: 'repeat', backgroundSize: '128px' }}></div>
      </div>

      {/* Decorative vertical accent line */}
      <div className="absolute left-10 top-0 bottom-0 w-px hidden lg:block" style={{ background: 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.25) 30%, rgba(212,175,55,0.25) 70%, transparent)' }}></div>

      {/* Gold bottom edge line */}
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.5) 30%, rgba(212,175,55,0.5) 70%, transparent)' }}></div>

      {/* Main Content */}
      <div
        className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center"
        style={{ minHeight: '100vh', paddingTop: '140px', paddingBottom: '120px' }}
      >
        <div className="max-w-[680px]">

          {/* Eyebrow */}
          <div
            className={`flex items-center gap-3 mb-10 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            style={{ transitionDelay: '80ms' }}
          >
            <div className="h-px w-12 bg-[#d4af37]"></div>
            <span
              className="text-[#d4af37] text-[10px] uppercase tracking-[4px] font-bold"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Campbell, CA &bull; South Bay &bull; Bay Area
            </span>
          </div>

          {/* H1 */}
          <h1
            className={`font-black text-white leading-[0.95] mb-8 transition-all duration-800 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'clamp(3.2rem, 7.5vw, 6rem)',
              transitionDelay: '180ms',
              letterSpacing: '-0.02em',
            }}
          >
            Your Roof.
            <br />
            <span className="text-[#d4af37]">Your Solar.</span>
            <br />
            Done Right.
          </h1>

          {/* Subheadline */}
          <p
            className={`text-white/60 leading-[1.75] mb-12 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(1rem, 1.4vw, 1.15rem)',
              maxWidth: '520px',
              transitionDelay: '320ms',
            }}
          >
            Shadowfax Roofing &amp; Solar delivers precision roof replacement, repair, gutter systems, and solar installation for South Bay homeowners — built to last, backed by integrity.
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-4 mb-16 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '460ms' }}
          >
            <button
              onClick={() => handleNav('#contact')}
              className="group flex items-center justify-center gap-3 text-[#1a1a1a] font-bold text-xs px-10 py-[18px] rounded transition-all duration-300 cursor-pointer whitespace-nowrap uppercase tracking-[2px]"
              style={{ fontFamily: "'Montserrat', sans-serif", background: '#d4af37' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#c4a030')}
              onMouseLeave={e => (e.currentTarget.style.background = '#d4af37')}
            >
              Get a Free Estimate
              <div className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300">
                <i className="ri-arrow-right-line text-base"></i>
              </div>
            </button>
            <a
              href="tel:4086280065"
              className="flex items-center justify-center gap-3 border border-white/25 hover:border-[#d4af37]/70 text-white hover:text-[#d4af37] font-semibold text-xs px-10 py-[18px] rounded transition-all duration-300 whitespace-nowrap uppercase tracking-[2px]"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-base"></i>
              </div>
              (408) 628-0065
            </a>
          </div>

          {/* Trust micro-signals */}
          <div
            className={`flex flex-wrap gap-x-8 gap-y-3 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '600ms' }}
          >
            {[
              { icon: 'ri-shield-check-line', text: 'Licensed & Insured' },
              { icon: 'ri-home-heart-line', text: 'Family-Owned' },
              { icon: 'ri-map-pin-2-line', text: 'Campbell, CA Based' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-[#d4af37] text-sm`}></i>
                </div>
                <span
                  className="text-white/45 text-[11px] uppercase tracking-[2px]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ transitionDelay: '900ms' }}
      >
        <span className="text-white/25 text-[9px] uppercase tracking-[3px]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-[#d4af37]/50 to-transparent"></div>
      </div>

      {/* Bottom stat bar */}
      <div className="absolute bottom-0 left-0 right-0 hidden lg:block" style={{ background: 'rgba(4,10,20,0.75)', backdropFilter: 'blur(12px)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-10 py-4 flex items-center gap-0">
          {[
            { icon: 'ri-time-line', value: 'Mon – Sat, 7am – 6pm', label: 'Business Hours' },
            { icon: 'ri-map-pin-2-line', value: '2959 Winchester Blvd Ste 102a', label: 'Campbell, CA 95008' },
            { icon: 'ri-phone-fill', value: '(408) 628-0065', label: 'Click to Call', href: 'tel:4086280065' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center">
              {i > 0 && <div className="w-px h-8 bg-white/10 mx-8"></div>}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${stat.icon} text-[#d4af37] text-base`}></i>
                </div>
                <div>
                  {stat.href ? (
                    <a href={stat.href} className="text-white text-sm font-semibold hover:text-[#d4af37] transition-colors" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.value}</a>
                  ) : (
                    <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.value}</p>
                  )}
                  <p className="text-white/35 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
